import React, { useEffect, useState } from "react";
import { api, apiErr, uploadImage } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ShoppingBag, Upload, GripVertical } from "lucide-react";

const CATS = ["Épicerie", "Souvenirs", "Autre"];
const empty = { name: "", category: "Épicerie", price: 0, description: "", image: "", order: 99, available: true };

export default function BoutiqueTab() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/products").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...edit, price: Number(edit.price) || 0, order: Number(edit.order) || 99 };
      if (edit.id) await api.put(`/admin/products/${edit.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Produit enregistré");
      setEdit(null);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Produit supprimé"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try { const url = await uploadImage(f); setEdit((s) => ({ ...s, image: url })); }
    catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); }
  };

  const input = "w-full rounded-xl border border-ink-400/25 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand";
  const label = "text-xs font-medium text-ink-600 mb-1 block";

  return (
    <div data-testid="boutique-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl text-ink-900 flex items-center gap-2"><ShoppingBag size={22} className="text-brand" /> Boutique</h2>
          <p className="text-sm text-ink-500 mt-1">Gérez les articles vendus à l'accueil (vitrine, sans paiement en ligne).</p>
        </div>
        <button onClick={() => setEdit({ ...empty, order: (items.length + 1) })} className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-5 py-2.5 text-sm font-medium hover:bg-brand-600" data-testid="add-product-btn">
          <Plus size={16} /> Ajouter un produit
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-black/5 overflow-hidden" data-testid={`admin-product-${p.id}`}>
            <div className="aspect-[16/10] bg-beige overflow-hidden">
              {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-ink-300"><ShoppingBag size={32} /></div>}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 text-[11px] text-ink-400 mb-1">
                <span className="inline-flex items-center gap-1"><GripVertical size={12} /> {p.order}</span>
                <span className="rounded-full bg-brand-50 text-brand px-2 py-0.5">{p.category}</span>
                {p.available === false && <span className="rounded-full bg-red-50 text-red-600 px-2 py-0.5">Masqué</span>}
              </div>
              <h3 className="font-medium text-ink-900">{p.name}</h3>
              <p className="text-brand font-heading text-lg mt-1">{Number(p.price || 0).toFixed(2)} €</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEdit(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-ink-400/25 py-2 text-sm hover:bg-black/5" data-testid={`edit-product-${p.id}`}><Pencil size={14} /> Modifier</button>
                <button onClick={() => remove(p.id)} className="p-2 rounded-lg text-red-500 hover:bg-red-50" data-testid={`delete-product-${p.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-ink-400 col-span-full py-10 text-center">Aucun produit pour le moment.</p>}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setEdit(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-cream rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4" data-testid="product-form">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl text-ink-900">{edit.id ? "Modifier le produit" : "Nouveau produit"}</h3>
              <button type="button" onClick={() => setEdit(null)} className="p-1.5 rounded-full hover:bg-black/5"><X size={18} /></button>
            </div>
            <div><label className={label}>Nom *</label><input required value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={input} data-testid="product-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Catégorie</label>
                <select value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className={input} data-testid="product-category">
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className={label}>Prix (€)</label><input type="number" step="0.5" min="0" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} className={input} data-testid="product-price" /></div>
            </div>
            <div><label className={label}>Description</label><textarea rows={2} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className={input} data-testid="product-desc" /></div>
            <div>
              <label className={label}>Photo</label>
              <div className="flex items-center gap-3">
                {edit.image && <img src={edit.image} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                <label className="inline-flex items-center gap-2 rounded-xl border border-dashed border-ink-400/40 px-4 py-2.5 text-sm text-ink-600 cursor-pointer hover:border-brand">
                  <Upload size={15} /> {uploading ? "Envoi…" : "Choisir une image"}
                  <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="product-image-upload" />
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Ordre</label><input type="number" value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} className={input} data-testid="product-order" /></div>
              <label className="flex items-center gap-2 text-sm text-ink-700 mt-6 cursor-pointer">
                <input type="checkbox" checked={edit.available !== false} onChange={(e) => setEdit({ ...edit, available: e.target.checked })} className="w-4 h-4 accent-brand" data-testid="product-available" />
                Visible sur le site
              </label>
            </div>
            <button type="submit" disabled={saving || uploading} className="w-full rounded-full bg-brand text-white py-3 font-medium hover:bg-brand-600 disabled:opacity-50" data-testid="save-product-btn">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
